import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@/components/ui/app-skeleton';
import { cn } from '@/lib/ui/utils';
import { WINNERS_ROW_GRID_CLASS } from './winners-grid';
const ROW_KEYS: readonly string[] = ['r0', 'r1', 'r2', 'r3', 'r4', 'r5', 'r6', 'r7', 'r8'];
const HEADER_COL_KEYS: readonly string[] = ['h0', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'h7'];
const META_KEYS: readonly string[] = ['m0', 'm1', 'm2', 'm3'];

/**
 * One Winners-feed row skeleton — mirrors `WinnersRow`'s responsive switch
 * exactly. At lg+ it's the 8-column grid table row (avatar + 6 numeric cells +
 * sparkline); below lg it's the tall (~200px) Launchpad-style stat card
 * (square avatar + 24h badge header, big market-cap headline + sparkline hero,
 * divider, and a 4-up Price / Volume / Graduated / Holders meta strip).
 */
function WinnersRowSkeleton() {
  const $ = _c(16);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cn("hidden lg:grid", WINNERS_ROW_GRID_CLASS, "rounded-card bg-background-primary px-4 py-4");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Skeleton className="size-10 shrink-0 rounded-control" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex min-w-0 items-center gap-3">{t1}<div className="flex min-w-0 flex-1 flex-col gap-1.5"><Skeleton className="h-3.5 w-3/4" /><Skeleton className="h-3 w-1/2" /></div></div>;
    t3 = <Skeleton className="h-3.5 w-12 justify-self-center" />;
    t4 = <Skeleton className="h-3.5 w-10 justify-self-center" />;
    t5 = <Skeleton className="h-3.5 w-12 justify-self-center" />;
    t6 = <Skeleton className="h-3.5 w-12 justify-self-center" />;
    t7 = <Skeleton className="h-3.5 w-10 justify-self-center" />;
    t8 = <Skeleton className="h-3.5 w-12 justify-self-center" />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
    t7 = $[7];
    t8 = $[8];
  }
  let t10;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className={t0}>{t2}{t3}{t4}{t5}{t6}{t7}{t8}<div className="flex h-9 items-center justify-end"><Skeleton className="h-9 w-28 rounded-control" /></div></div>;
    t10 = cn("flex flex-col gap-3 lg:hidden", "rounded-card bg-background-primary px-4 py-4");
    $[9] = t10;
    $[10] = t9;
  } else {
    t10 = $[9];
    t9 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Skeleton className="size-12 shrink-0 rounded-overlay-item" />;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex items-center gap-3">{t11}<div className="flex min-w-0 flex-1 flex-col gap-1.5"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-2/5" /></div><Skeleton className="h-7 w-16 shrink-0 rounded-pill" /></div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="flex items-center justify-between gap-3"><div className="flex min-w-0 flex-col gap-1.5"><Skeleton className="h-7 w-28" /><Skeleton className="h-2.5 w-16" /></div><Skeleton className="h-11 w-32 shrink-0 rounded-control" /></div>;
    t14 = <div className="h-px w-full bg-border-primary" aria-hidden="true" />;
    $[13] = t13;
    $[14] = t14;
  } else {
    t13 = $[13];
    t14 = $[14];
  }
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <>{t9}<div className={t10}>{t12}{t13}{t14}<div className="grid grid-cols-4 gap-2">{META_KEYS.map(_temp)}</div></div></>;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  return t15;
}

/**
 * The Winners-feed body skeleton — the desktop column-header stripe plus the
 * responsive stack of `WinnersRowSkeleton` rows. Shared by the route-level
 * `loading.tsx` fallback and `WinnersFeed`'s own cold/filtered `isPending`
 * branch, so a cache miss inside the feed renders the SAME skeleton as the
 * route shell instead of a divergent stripe.
 */
function _temp(key) {
  return <div key={key} className="flex flex-col items-center gap-1.5"><Skeleton className="h-2.5 w-3/4" /><Skeleton className="h-3.5 w-1/2" /></div>;
}
export function WinnersFeedSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={cn("hidden lg:grid", WINNERS_ROW_GRID_CLASS, "items-end px-4 pb-4 pt-4")}>{HEADER_COL_KEYS.map(_temp2)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex flex-col gap-0">{t0}<ul className="flex flex-col gap-2">{ROW_KEYS.map(_temp3)}</ul></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp3(key_0) {
  return <li key={key_0} className="min-w-0"><WinnersRowSkeleton /></li>;
}
function _temp2(key, idx) {
  return <Skeleton key={key} className={cn("h-3 w-12", idx === 0 ? "justify-self-start" : "justify-self-center")} />;
}